/**
 * The command envelope, the error type, and the reducer's result type.
 *
 * Every write in the product is one of these envelopes (PLAN §8.3): the UI
 * dispatches them, the outbox queues them, the Durable Object replays them, and
 * the AI assistant proposes batches of them. They are plain JSON — no `Date`,
 * no `Map`, no class instances — so `JSON.parse(JSON.stringify(cmd))` is
 * lossless.
 */

import { z } from 'zod';
import { documentIdSchema, timestampSchema } from '../document/common';
import type { WorkspaceState } from '../document/workspace';

/**
 * Who issued a command.
 *
 * Recorded because the assistant is a first-class writer (PLAN §7) and the
 * history/undo UI needs to distinguish "you changed this" from "the assistant
 * changed this, and you accepted it". Never used to grant permission: an
 * assistant command is applied only after the user accepts the changeset, at
 * which point it goes through this exact reducer.
 */
export const actorSchema = z.enum(['user', 'assistant']);

/** @see actorSchema */
export type Actor = z.infer<typeof actorSchema>;

/** The envelope fields every command carries, independent of its payload. */
export const commandEnvelopeBaseSchema = z.strictObject({
  /**
   * Client-generated unique id.
   *
   * The sync outbox dedupes on it, so a command redelivered after a reconnect
   * applies exactly once (PLAN §10, "chaos tests for reconnect/out-of-order/
   * duplicate delivery").
   */
  commandId: documentIdSchema,
  /** The command name, e.g. `recipe.addHop`. */
  type: z.string().min(1).max(64),
  /**
   * Client wall-clock time the command was issued, **ms since epoch**.
   *
   * Supplied by the caller and used for every `createdAt`/`updatedAt` the
   * reducer writes — which is precisely why nothing inside this package reads a
   * clock, and why replaying a log reproduces a byte-identical workspace.
   */
  timestamp: timestampSchema,
  /** @see actorSchema */
  actor: actorSchema,
  /** Command-specific data, validated by the command's own schema. */
  payload: z.unknown(),
});

/** One command: envelope plus its typed, validated payload. */
export interface CommandEnvelope<TType extends string, TPayload> {
  /** Client-generated unique id; the sync outbox dedupes on it. */
  readonly commandId: string;
  /** The command name. */
  readonly type: TType;
  /** Command-specific data. */
  readonly payload: TPayload;
  /** Client wall-clock time, ms since epoch. */
  readonly timestamp: number;
  /** Who issued it. */
  readonly actor: Actor;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Why a command could not be applied.
 *
 * - `unknown-command` — the `type` is not in the registry. Happens when an old
 *   client replays a log written by a newer one.
 * - `malformed-envelope` — not even shaped like a command.
 * - `invalid-payload` — the payload failed its Zod schema; {@link
 *   CommandError.issues} carries the field-level detail.
 * - `not-found` — the recipe/profile/item the command addresses does not exist.
 * - `conflict` — the id the command wants to create is already taken.
 * - `invalid-state` — the command is well-formed but meaningless here (salt
 *   additions on a recipe with no water section, editing a built-in profile).
 * - `in-use` — the document the command would remove is still referenced by
 *   others: a custom ingredient with lots or recipe rows linked to it
 *   (`customIngredient.remove`, PLAN-INGREDIENT-LINKS B2). Distinct from
 *   `invalid-state` because the client answers it with a dialog that names the
 *   users and offers the one cascade that exists, not with an error toast; the
 *   message carries the counts so a caller with no state (the assistant, a
 *   sync log) can still say what was in the way.
 * - `plan-limit` — the command is valid and the *account* refuses it. **Never
 *   returned by the reducer in this package**: the reducer is pure and knows
 *   nothing about accounts. It is produced by the Durable Object at apply time,
 *   for a `recipe.create` past the trial cap (PLAN §9), and it lives in this
 *   union because `CommandError` is the shape the sync protocol carries and the
 *   client's reconciliation already understands. The client distinguishes it
 *   from the codes above to show an upgrade dialog rather than an error toast.
 * - `recipe-shared` — this recipe now lives in a room; your command was passed
 *   on to it (PLAN-COLLAB). Like `plan-limit`, **never returned by the reducer
 *   in this package**: the workspace Durable Object answers it for a command
 *   that addresses a recipe the workspace holds only a reference to, and the
 *   client treats it as settled rather than as a failure.
 */
export type CommandErrorCode =
  | 'unknown-command'
  | 'malformed-envelope'
  | 'invalid-payload'
  | 'not-found'
  | 'conflict'
  | 'invalid-state'
  | 'in-use'
  | 'plan-limit'
  | 'recipe-shared';

/** One field-level validation problem, flattened out of a Zod error. */
export interface CommandIssue {
  /** Dotted path into the payload, e.g. `item.amountKg`. */
  readonly path: string;
  /** Human-readable explanation. */
  readonly message: string;
}

/**
 * A typed failure. The reducer returns one of these; it never throws mid-reduce
 * and never leaves a half-applied state behind.
 */
export interface CommandError {
  readonly code: CommandErrorCode;
  /** One line, safe to show a user or log verbatim. */
  readonly message: string;
  /** The offending command's id, when it had a readable one. */
  readonly commandId: string | null;
  /** The offending command's type, when it had a readable one. */
  readonly commandType: string | null;
  /** Field-level detail for `invalid-payload`. */
  readonly issues: readonly CommandIssue[];
  /** Position in the batch, for `applyLog` / `applyChangeset` failures. */
  readonly index: number | null;
}

/** The reducer's result: a new state, or a typed error. Never a throw. */
export type ApplyResult =
  | { readonly ok: true; readonly state: WorkspaceState }
  | { readonly ok: false; readonly error: CommandError };

/** Build a {@link CommandError}, filling the fields a caller usually leaves out. */
export function commandError(
  code: CommandErrorCode,
  message: string,
  extra: {
    readonly commandId?: string | null;
    readonly commandType?: string | null;
    readonly issues?: readonly CommandIssue[];
    readonly index?: number | null;
  } = {},
): CommandError {
  return {
    code,
    message,
    commandId: extra.commandId ?? null,
    commandType: extra.commandType ?? null,
    issues: extra.issues ?? [],
    index: extra.index ?? null,
  };
}

/** Flatten a Zod error into {@link CommandIssue}s, sorted for deterministic output. */
export function issuesFromZod(error: z.ZodError): readonly CommandIssue[] {
  return error.issues
    .map((issue) => ({ path: issue.path.join('.'), message: issue.message }))
    .sort((a, b) =>
      a.path === b.path ? a.message.localeCompare(b.message) : a.path.localeCompare(b.path),
    );
}
